import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { api } from './api';

interface User {
    email: string;
}

interface AuthContextType {
    user: User | null;
    loading: boolean;
    login: (email: string, code: string) => Promise<void>;
    logout: () => Promise<void>;
    requestOTP: (email: string) => Promise<{ success: boolean; error?: string }>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        checkAuth();
    }, []);

    async function checkAuth() {
        try {
            const response = await api.get<{ success: boolean; user?: User }>('/api/auth/me');
            if (response.success && response.user) {
                setUser(response.user);
            }
        } catch (error) {
            // Not authenticated
        } finally {
            setLoading(false);
        }
    }

    async function requestOTP(email: string): Promise<{ success: boolean; error?: string }> {
        try {
            const response = await api.post<{ success: boolean; error?: string; message?: string }>(
                '/api/auth/request-otp',
                { email }
            );
            return response;
        } catch (error) {
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Failed to send code'
            };
        }
    }

    async function login(email: string, code: string) {
        const response = await api.post<{ success: boolean; error?: string; user?: User }>(
            '/api/auth/verify-otp',
            { email, code }
        );

        if (!response.success) {
            throw new Error(response.error || 'Login failed');
        }

        if (response.user) {
            setUser(response.user);
        }
    }

    async function logout() {
        await api.post('/api/auth/logout', {});
        setUser(null);
    }

    return (
        <AuthContext.Provider value={{ user, loading, login, logout, requestOTP }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
}
